<template>
  <div class="company-websites" data-testid="company-websites">
    <div v-if="showHeading || canScanNow" class="company-websites__header">
      <div v-if="showHeading" class="company-websites__heading">
        <span id="company-websites-label" class="company-websites__label"> Company websites </span>
        <p id="company-websites-helper" class="company-websites__helper">
          Add the public websites of your company. IQ scans them and stores what it finds as account context.
        </p>
      </div>
      <StandardButton
        v-if="canScanNow"
        type="magenta"
        variant="outline"
        size="small"
        :label="scanNowLabel"
        :disabled="scanNowDisabled"
        data-testid="company-websites-scan-now"
        @click="emit('scan-now')"
      />
    </div>

    <ul
      v-if="websites.length"
      class="company-websites__list"
      :aria-labelledby="showHeading ? 'company-websites-label' : null"
      :aria-label="showHeading ? null : 'Company websites'"
      data-testid="company-websites-list"
    >
      <li v-for="website in websites" :key="website" class="company-websites__item">
        <div class="company-websites__row">
          <span class="company-websites__url">{{ website }}</span>
          <button
            type="button"
            class="company-websites__remove"
            :disabled="isBusy"
            :aria-label="`Remove ${website}`"
            data-testid="company-websites-remove"
            @click="emit('remove', website)"
          >
            Remove
          </button>
        </div>
        <p
          v-if="scanNotes[website]"
          class="company-websites__scan-note"
          data-testid="company-websites-scan-note"
        >
          {{ scanNotes[website] }}
        </p>
      </li>
    </ul>

    <div class="company-websites__add">
      <input
        v-model="newWebsite"
        type="url"
        class="company-websites__input"
        :class="{ 'company-websites__input--error': !!localError }"
        placeholder="https://www.example.com"
        :aria-labelledby="inputLabelledby"
        :aria-label="inputLabelledby ? null : 'Company website'"
        :aria-describedby="showHeading ? 'company-websites-helper' : null"
        :disabled="isBusy"
        data-testid="company-websites-input"
        @keydown.enter.prevent="handleAdd"
      />
      <StandardButton
        type="magenta"
        :variant="wizardStyle ? 'primary' : 'secondary'"
        size="small"
        label="Add"
        :class="{ 'company-websites__add-btn--wizard': wizardStyle }"
        :disabled="isBusy || !canAdd"
        data-testid="company-websites-add"
        @click="handleAdd"
      />
    </div>

    <p v-if="saving" class="company-websites__status" role="status">Saving…</p>
    <p
      v-if="shownError"
      class="company-websites__error"
      role="alert"
      data-testid="company-websites-error"
    >
      {{ shownError }}
    </p>
  </div>
</template>

<script setup>
import { computed, ref } from "vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";
import { isHttpWebsiteUrl } from "@components/utils/companyWebsites";
import { formatStatusDate } from "@components/utils/formatStatusDate";

const props = defineProps({
  websites: {
    type: Array,
    default: () => [],
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  // ER-12778: the "Company websites" heading and helper are Settings copy; the welcome wizard hides them.
  showHeading: {
    type: Boolean,
    default: true,
  },
  saving: {
    type: Boolean,
    default: false,
  },
  // ER-12993: the welcome wizard's Add button (40px, primary); Settings keeps the small secondary button.
  wizardStyle: {
    type: Boolean,
    default: false,
  },
  // ER-12993: id of an external visible label that names the input (the wizard renders its own).
  labelledby: {
    type: String,
    default: "",
  },
  // ER-12781: scan status per website URL, `{ [url]: { status, last_scanned_at } }`. Empty hides the notes.
  scanStatuses: {
    type: Object,
    default: () => ({}),
  },
  // ER-12785: owners and co-owners only; the parent decides (flag + role). Never set in the wizard.
  canScanNow: {
    type: Boolean,
    default: false,
  },
  // ER-12785: the Scan now request is in flight.
  scanRequesting: {
    type: Boolean,
    default: false,
  },
  // ER-10219: API field error for company_websites, shown inline.
  error: {
    type: String,
    default: "",
  },
});

const emit = defineEmits(["add", "remove", "scan-now"]);

const newWebsite = ref("");
const localError = ref("");

const isBusy = computed(() => props.disabled || props.saving);
const canAdd = computed(() => isHttpWebsiteUrl(newWebsite.value));
const shownError = computed(() => localError.value || props.error);
const inputLabelledby = computed(
  () => props.labelledby || (props.showHeading ? "company-websites-label" : null)
);

const isScanning = computed(
  () =>
    props.scanRequesting ||
    Object.values(props.scanStatuses || {}).some((scan) => scan?.status === "scanning")
);
const scanNowLabel = computed(() => (isScanning.value ? "Scanning…" : "Scan now"));
const scanNowDisabled = computed(
  () => isScanning.value || isBusy.value || props.websites.length === 0
);

const SCAN_NOTES = {
  scanning: "IQ is reading this website.",
  unreachable: "IQ could not read this website.",
};

function scanNoteFor(scan) {
  if (scan?.status === "scanned") {
    const date = formatStatusDate(scan.last_scanned_at);
    return date ? `Last scanned ${date}` : "";
  }
  return SCAN_NOTES[scan?.status] || "";
}

const scanNotes = computed(() =>
  Object.fromEntries(
    props.websites.map((website) => [website, scanNoteFor(props.scanStatuses[website])])
  )
);

function handleAdd() {
  const value = newWebsite.value.trim();
  if (!value) {
    return;
  }
  if (!isHttpWebsiteUrl(value)) {
    localError.value = "Enter a valid http(s) website URL.";
    return;
  }
  if (props.websites.includes(value)) {
    localError.value = "This website has already been added.";
    return;
  }
  localError.value = "";
  emit("add", value);
  newWebsite.value = "";
}
</script>

<style scoped>
.company-websites {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
}

.company-websites__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.company-websites__heading {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.company-websites__label {
  font-family: "Inter", sans-serif;
  font-weight: 500;
  font-size: 15px;
  line-height: 22px;
  color: #101828;
}

.company-websites__helper {
  margin: 0;
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 13px;
  line-height: 20px;
  color: #475467;
}

.company-websites__list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.company-websites__item {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 10px 14px;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  background: #ffffff;
}

.company-websites__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.company-websites__url {
  min-width: 0;
  overflow-wrap: anywhere;
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 22px;
  color: #101828;
}

.company-websites__scan-note {
  margin: 0;
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 13px;
  line-height: 20px;
  color: #475467;
}

.company-websites__remove {
  flex-shrink: 0;
  padding: 0;
  border: none;
  background: transparent;
  font-family: "Inter", sans-serif;
  font-weight: 500;
  font-size: 13px;
  line-height: 20px;
  color: #d92d20;
  cursor: pointer;
}

.company-websites__remove:disabled {
  color: #98a2b3;
  cursor: not-allowed;
}

.company-websites__add {
  display: flex;
  align-items: center;
  gap: 8px;
}

.company-websites__add .company-websites__add-btn--wizard.standard-button--primary {
  box-sizing: border-box;
  height: 40px;
  padding: 0 14px;
  font-family: inherit;
}

.company-websites__add .company-websites__add-btn--wizard.standard-button--primary.standard-button--disabled {
  background-color: #f2f4f7;
  border-color: #eaecf0;
  color: #98a2b3;
}

.company-websites__input {
  flex: 1;
  min-width: 0;
  height: 40px;
  box-sizing: border-box;
  padding: 0 12px;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 22px;
  color: #101828;
  background: #ffffff;
  outline: none;
}

.company-websites__input:focus {
  border-color: #7f56d9;
}

.company-websites__input--error {
  border-color: #f04438;
}

.company-websites__input:disabled {
  color: #98a2b3;
  background: #f9fafb;
  cursor: not-allowed;
}

.company-websites__status {
  margin: 0;
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 17px;
  color: #667085;
}

.company-websites__error {
  margin: 0;
  font-family: "Inter", sans-serif;
  font-weight: 500;
  font-size: 12px;
  line-height: 17px;
  color: #d92d20;
}
</style>
